import { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";

/**
 * CSS variable read by other layout primitives (e.g. the Toast host) so
 * floating elements at the bottom of the page can clear the action bar.
 */
const STICKY_BAR_HEIGHT_VAR = "--sticky-action-bar-height";

export type Props = {
  /** Primary action button config (e.g., Save) */
  primaryAction: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    visuallyDisabled?: boolean;
    isLoading?: boolean;
  };
  /** Secondary action button config (e.g., Cancel) - optional */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  /** Additional CSS classes */
  className?: string;
  /** When true, buttons stretch to fill the full width */
  fullWidth?: boolean;
};

/**
 * Sticky action bar that displays primary/secondary actions at the bottom of the viewport.
 * Positioned above the footer, with consistent width/padding matching page content.
 */
export function StickyActionBar({
  primaryAction,
  secondaryAction,
  className,
  fullWidth = false,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  // Publish the bar's height as a CSS variable on the document root so
  // floating elements (e.g. toasts) can position themselves above it. Falls
  // back to 0 when no bar is mounted.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      document.documentElement.style.setProperty(
        STICKY_BAR_HEIGHT_VAR,
        `${el.offsetHeight}px`
      );
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(STICKY_BAR_HEIGHT_VAR);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={twMerge(
        "sticky bottom-0 left-0 right-0 z-50 w-full bg-brand-50/90 border-t border-brand-100 py-4",
        className,
      )}
      data-testid="sticky-action-bar"
    >
      <div
        className={twMerge(
          "flex flex-row gap-2 md:gap-4 justify-end max-w-3xl w-full mx-auto pl-4 md:px-8",
          fullWidth && "max-w-none px-0 md:px-0 pl-0",
        )}
      >
        {secondaryAction && (
          <Button
            variant="secondary"
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled}
            data-testid="sticky-action-bar-secondary"
            className={twMerge("min-w-24 md:w-auto", fullWidth && "flex-1")}
          >
            {secondaryAction.label}
          </Button>
        )}
        <Button
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
          visuallyDisabled={primaryAction.visuallyDisabled}
          isLoading={primaryAction.isLoading}
          data-testid="sticky-action-bar-primary"
          className={twMerge("min-w-24 md:w-auto", fullWidth && "flex-1")}
        >
          {primaryAction.label}
        </Button>
      </div>
    </div>
  );
}

export default StickyActionBar;
